class ProductCampaign {
	constructor(options) {
		this.opts = ProductCampaign.normalizeOptions(options);
		this.matchedByEl = new WeakMap();
		this.mo = null;
	}

	run() {
		if (!ProductCampaign.isWithinInterval(this.opts.dateFrom, this.opts.dateTo)) return;
		const elements = document.querySelectorAll(`[${this.opts.dataAttr}]`);
		if (elements.length === 0) return;

		for (const el of elements) {
			let ids;
			try {
				ids = JSON.parse(el.getAttribute(this.opts.dataAttr) || "[]");
			} catch {
				continue;
			}
			if (!Array.isArray(ids)) continue;

			const matches = ids.map((id) => String(id)).filter((id) => this.opts.collectionsSet.has(id));
			if (matches.length === 0) continue;

			let seen = this.matchedByEl.get(el);
			if (!seen) {
				seen = new Set();
				this.matchedByEl.set(el, seen);
			}

			const deduped = matches.filter((m) => !seen.has(m));
			if (deduped.length === 0) continue;

			this.opts.onMatch?.(el, {
				ids,
				matches: deduped,
				options: this.opts,
				utils: {
					insertHtml: ProductCampaign.insertHtml,
					escapeHtml: ProductCampaign.escapeHtml,
				},
			});

			for (const m of deduped) seen.add(m);
		}
	}

	start() {
		if (!ProductCampaign.isWithinInterval(this.opts.dateFrom, this.opts.dateTo)) {
			return { run: () => {}, destroy: () => {} };
		}

		const handler = ProductCampaign.debounce(() => this.run(), this.opts.debounceMs);
		this.cleanups = ProductCampaign.attachEvents(this.opts.events, handler);

		if (this.opts.observe) {
			this.mo = new MutationObserver(() => handler());
			this.mo.observe(document.documentElement, { childList: true, subtree: true });
		}

		if (document.readyState !== "loading" || !this.opts.events.includes("DOMContentLoaded")) {
			handler();
		}

		return { run: () => this.run(), destroy: () => this.destroy() };
	}

	destroy() {
		if (Array.isArray(this.cleanups)) {
			for (const off of this.cleanups) off();
		}
		if (this.mo) this.mo.disconnect();
	}

	static normalizeOptions(input) {
		const collections = ProductCampaign.normalizeCollections(input);
		return {
			collections,
			collectionsSet: new Set(collections.map((x) => String(x))),
			dateFrom: String(input.dateFrom),
			dateTo: String(input.dateTo),
			dataAttr: input.dataAttr || "data-product-collections",
			onMatch: input.onMatch || (() => {}),
			events: Array.isArray(input.events) && input.events.length > 0 ? input.events : ["DOMContentLoaded"],
			observe: Boolean(input.observe || false),
			debounceMs: Number.isFinite(input.debounceMs) ? Number(input.debounceMs) : 50,
		};
	}

	static normalizeCollections(input) {
		if (Array.isArray(input.collections)) return input.collections;
		if (input.collection != null) return [input.collection];
		return [];
	}

	static isWithinInterval(fromStr, toStr) {
		const from = new Date(`${fromStr}T00:00:00`);
		const to = new Date(`${toStr}T23:59:59.999`);
		const now = new Date();
		return now >= from && now <= to;
	}

	static debounce(fn, ms) {
		let t;
		return function () {
			clearTimeout(t);
			t = setTimeout(() => fn.apply(this, arguments), ms);
		};
	}

	static attachEvents(events, handler) {
		const offs = [];
		for (const type of events) {
			if (type === "DOMContentLoaded") {
				const l = () => handler();
				document.addEventListener(type, l);
				offs.push(() => document.removeEventListener(type, l));
				continue;
			}
			const ld = () => handler();
			document.addEventListener(type, ld);
			offs.push(() => document.removeEventListener(type, ld));
			const lw = () => handler();
			window.addEventListener(type, lw);
			offs.push(() => window.removeEventListener(type, lw));
		}
		return offs;
	}

	static escapeHtml(text) {
		const map = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
		return String(text).replace(/[&<>"']/g, (ch) => map[ch]);
	}

	static insertHtml(target, html, position = "afterend") {
		if (!target) return;
		target.insertAdjacentHTML(position, html);
	}
}

window.ProductCampaign = window.ProductCampaign ? window.ProductCampaign : ProductCampaign;